import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import "./HistoricoCompraADM.css";

import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  Grid,
  MenuItem,
  Divider,
  Typography,
  Pagination,
  Alert,
  Box
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import ModalBaixarNota from './ModalBaixarNota';
import api from '../../../services/api';
import API_CONFIG from '../../../config/api.config';

// Configurar plugins do dayjs
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const HistoricoCompraADM = () => {
  const navigate = useNavigate();
  const [isMenuExpanded, setIsMenuExpanded] = useState(false);
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');
  const [fornecedorSelecionado, setFornecedorSelecionado] = useState('');
  const [compras, setCompras] = useState([]);
  const [comprasFiltradas, setComprasFiltradas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 600);
  const [modalNotaOpen, setModalNotaOpen] = useState(false);
  const [notaSelecionada, setNotaSelecionada] = useState({ numero: '', compraId: null });
  const [numeroCompra, setNumeroCompra] = useState('');

  // Estados para paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  // Lista de fornecedores únicos para o filtro
  const [fornecedores, setFornecedores] = useState([]);

  // Verificação de autenticação e tipo de usuário
  useEffect(() => {
    const tipoUsuario = localStorage.getItem('tipoUsuario');
    const idUsuario = localStorage.getItem('idUsuario');

    if (!tipoUsuario || !idUsuario) {
      //console.log('Usuário não autenticado - Redirecionando para login');
      navigate('/', { replace: true });
      return;
    }

    if (tipoUsuario !== 'ADM') {
      //console.log(`Tipo de usuário (${tipoUsuario}) não corresponde ao permitido (ADM)`);
      navigate('/', { replace: true });
      return;
    }

    //console.log('ADM autenticado:', idUsuario);
  }, [navigate]);

  useEffect(() => {
    const handleMenuChange = (event) => {
      setIsMenuExpanded(!event.detail.isCollapsed);
    };
    window.addEventListener('menuStateChange', handleMenuChange);
    return () => window.removeEventListener('menuStateChange', handleMenuChange);
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 600);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Buscar dados da API
  const buscarCompras = async (page = 1) => {
    try {
      setLoading(true);
      setError('');

      const params = {
        page
      };

      // Adiciona filtros de data apenas se estiverem preenchidos
      if (dataInicio) {
        params.data_inicio = dataInicio;
      }
      if (dataFim) {
        params.data_fim = dataFim;
      }

      const response = await api.get(API_CONFIG.ENDPOINTS.COMPRAS.LISTAR_TODAS_COMPRAS, {
        params
      });

      if (response.data.status && response.data.compras) {
        const comprasData = response.data.compras;
        setCompras(comprasData.data || []);
        setTotalPages(comprasData.last_page || 1);
        setTotalItems(comprasData.total || 0);
        setCurrentPage(comprasData.current_page || 1);

        // Extrair fornecedores únicos dos dados
        const fornecedoresUnicos = [...new Set(
          comprasData.data.map(compra =>
            compra.cotacao?.fornecedor?.FOR_RAZAO_SOCIAL || 'Fornecedor não informado'
          )
        )];
        setFornecedores(fornecedoresUnicos);

        // Aplicar filtro de fornecedor se selecionado
        aplicarFiltroFornecedor(comprasData.data, fornecedorSelecionado);
      } else {
        setCompras([]);
        setComprasFiltradas([]);
        setFornecedores([]);
      }
    } catch (error) {
     // console.error('Erro ao buscar compras:', error);
      setError('Erro ao carregar histórico de compras. Tente novamente.');
      setCompras([]);
      setComprasFiltradas([]);
    } finally {
      setLoading(false);
    }
  };

  // Função para aplicar filtro de fornecedor localmente
  const aplicarFiltroFornecedor = (listaCompras, fornecedor) => {
    let listaFiltrada = listaCompras;
    // Filtro por número da compra
    if (numeroCompra) {
      listaFiltrada = listaFiltrada.filter(compra =>
        compra.COM_ID && compra.COM_ID.toString().includes(numeroCompra)
      );
    }
    if (!fornecedor) {
      setComprasFiltradas(listaFiltrada);
    } else {
      const filtradas = listaFiltrada.filter(compra => {
        const nomeFornecedor = compra.cotacao?.fornecedor?.FOR_RAZAO_SOCIAL;
        return nomeFornecedor && nomeFornecedor.toLowerCase().includes(fornecedor.toLowerCase());
      });
      setComprasFiltradas(filtradas);
    }
  };

  // Aplica os filtros
  useEffect(() => {
    aplicarFiltroFornecedor(compras, fornecedorSelecionado);
  }, [compras, fornecedorSelecionado, numeroCompra]);

  // Buscar dados iniciais
  useEffect(() => {
    buscarCompras();
  }, []);

  // Buscar dados quando mudar página
  useEffect(() => {
    buscarCompras(currentPage);
  }, [currentPage, dataInicio, dataFim]);

  const formatarValor = (valor) => {
    if (!valor) return 'R$ 0,00';
    return Number(valor).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };

  const formatarData = (dataString) => {
    if (!dataString) return '---';
    const data = new Date(dataString);
    return data.toLocaleDateString('pt-BR');
  };

  const calcularValorTotal = (itens) => {
    if (!itens || itens.length === 0) return 0;
    return itens.reduce((total, item) => {
      const valor = Number(item.item_compra?.ITC_VALOR_REVISADO || item.item_compra?.ITC_VALOR_ORIGINAL || 0);
      const quantidade = Number(item.IT_QUANTIDADE || 0);
      return total + (valor * quantidade);
    }, 0);
  };

  const handleAbrirModalNota = (compra) => {
    setNotaSelecionada({
      numero: compra.COM_NOTA_NUMERO || '---',
      compraId: compra.COM_ID
    });
    setModalNotaOpen(true);
  };

  const handleFecharModalNota = () => {
    setModalNotaOpen(false);
    setNotaSelecionada({ numero: '', compraId: null });
  };

  const handlePageChange = (event, newPage) => {
    setCurrentPage(newPage);
  };

  const handleFiltrar = () => {
    setCurrentPage(1);
    buscarCompras(1);
  };

  return (
    <div className={`historico-compra-adm-container ${isMenuExpanded ? 'menu-expanded' : ''}`}>
      <div className="historico-compra-adm-wrapper">
        <h1 className="historico-compra-adm-title">Histórico de Compras</h1>
        <div className="historico-compra-adm-content">
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Grid
            container
            spacing={1}
            alignItems="center"
            justifyContent="center"
            sx={{ mb: 2 }}
          >
            <Grid item xs={12} sm="auto">
              <TextField
                label="Data Início"
                type="date"
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
                value={dataInicio}
                onChange={(e) => setDataInicio(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm="auto">
              <TextField
                label="Data Fim"
                type="date"
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
                value={dataFim}
                onChange={(e) => setDataFim(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm="auto">

</Grid>
            <Grid item xs={12} sm="auto">
              <TextField
                select
                label="Fornecedor"
                fullWidth
                size="small"
                value={fornecedorSelecionado}
                onChange={(e) => setFornecedorSelecionado(e.target.value)}
              >
                <MenuItem value="">Todos</MenuItem>
                {fornecedores.map((fornecedor, index) => (
                  <MenuItem key={index} value={fornecedor}>
                    {fornecedor}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm="auto">
              <Button
                variant="contained"
                startIcon={<FilterAltIcon />}
                onClick={handleFiltrar}
                className="historico-compra-adm-filter-button"
                disabled={loading}
                fullWidth
                style={{ minWidth: 220, maxWidth: 300, height: 40 }}
              >
                {loading ? 'Carregando...' : 'Filtrar'}
              </Button>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          {isMobile ? (
            <div>
              {comprasFiltradas.length > 0 ? (
                comprasFiltradas.map((compra) => {
                  const valorTotal = calcularValorTotal(compra.itens);
                  const fornecedorNome = compra.cotacao?.fornecedor?.FOR_RAZAO_SOCIAL || 'Fornecedor não informado';
                  const notaEnviada = compra.COM_NOTA_ENVIADA === 'SIM';

                  return (
                    <Paper key={compra.COM_ID} className="historico-compra-adm-card" elevation={2} sx={{ mb: 2, p: 2 }}>
                      <div><b>Nº Compra:</b> {compra.COM_ID}</div>
                      <div><b>Fornecedor:</b> {fornecedorNome}</div>
                      <div><b>Data:</b> {formatarData(compra.COM_DATA_VINCULACAO)}</div>
                      <div><b>Valor Total:</b> {formatarValor(valorTotal)}</div>
                      <div><b>Status:</b> {compra.cotacao?.COT_STATUS || '---'}</div>
                      <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handleAbrirModalNota(compra)}
                          disabled={!notaEnviada}
                          sx={{ minWidth: '120px' }}
                        >
                          {notaEnviada ? 'Baixar Nota Fiscal' : 'Nota não enviada'}
                        </Button>
                      </div>
                    </Paper>
                  );
                })
              ) : (
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                  {loading ? 'Carregando...' : 'Nenhuma compra encontrada com os filtros aplicados'}
                </Paper>
              )}
            </div>
          ) : (
            <TableContainer component={Paper} className="historico-compra-adm-table-container">
              <Table stickyHeader size="small" className="historico-compra-adm-table">
                <TableHead>
                  <TableRow className="historico-compra-adm-table-header">
                    <TableCell width="10%">Nº Compra</TableCell>
                    <TableCell width="25%">Fornecedor</TableCell>
                    <TableCell width="20%">Data</TableCell>
                    <TableCell width="20%" >Valor Total</TableCell>
                    <TableCell width="20%">Status</TableCell>
                    <TableCell width="20%" align="center">Nota Fiscal</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        Carregando...
                      </TableCell>
                    </TableRow>
                  ) : comprasFiltradas.length > 0 ? (
                    comprasFiltradas.map((compra) => {
                      const valorTotal = calcularValorTotal(compra.itens);
                      const fornecedorNome = compra.cotacao?.fornecedor?.FOR_RAZAO_SOCIAL || 'Fornecedor não informado';
                      const notaEnviada = compra.COM_NOTA_ENVIADA === 'SIM';

                      return (
                        <TableRow key={compra.COM_ID} hover>
                          <TableCell width="10%">{compra.COM_ID}</TableCell>
                          <TableCell width="25%">{fornecedorNome}</TableCell>
                          <TableCell width="20%">{formatarData(compra.COM_DATA_VINCULACAO)}</TableCell>
                          <TableCell width="20%" >{formatarValor(valorTotal)}</TableCell>
                          <TableCell width="20%">{compra.cotacao?.COT_STATUS || '---'}</TableCell>
                          <TableCell width="20%" align="center">
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => handleAbrirModalNota(compra)}
                              disabled={!notaEnviada}
                            >
                              {notaEnviada ? 'Baixar Nota Fiscal' : 'Nota não enviada'}
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} align="center" className="historico-compra-adm-no-results">
                        {loading ? 'Carregando...' : 'Nenhuma compra encontrada com os filtros aplicados'}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Paginação */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Box>
          )}

          {/* Informações de paginação */}
          {totalItems > 0 && (
            <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
              Mostrando {comprasFiltradas.length} de {totalItems} compras
            </Typography>
          )}
        </div>
      </div>
      <ModalBaixarNota
        open={modalNotaOpen}
        onClose={handleFecharModalNota}
        numeroNota={notaSelecionada.numero}
        compraId={notaSelecionada.compraId}
        onDownload={() => {
          // Callback após download
          //console.log('Download concluído');
        }}
      />
    </div>
  );
};

export default HistoricoCompraADM;